import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, MapPin, Home, Maximize2, Wrench, CalendarClock, Coins, BarChart3, Tag, Layers, TrendingUp, FileText,
  Handshake, UserRound, Info, X, Loader2, CheckCircle2, Building2, ListChecks,
} from 'lucide-react';
import { EmptyState, PageSkeleton } from '@/components/dashboard/MetricCard';
import FundingBar from '@/components/opportunities/FundingBar';
import {
  opportunitiesApi, STATUS_STYLE, imgUrl, money, opTitle, opPlace, returnFigure, saleFigure, DISCLAIMER,
} from '@/services/opportunitiesApi';

/*
  Ficha completa de una oportunidad (/dashboard/propiedades/:id): fotos, ficha técnica, cifras estimadas,
  estructura de costes, estudio de mercado y aportaciones (barra de progreso, mínimo y solicitud de participación).
*/

const card = 'rounded-2xl border border-white/[0.06] bg-white/[0.02]';

function Gallery({ images, title }) {
  const [i, setI] = useState(0);
  if (!images.length) {
    return <div className={`${card} aspect-[16/10] flex items-center justify-center`}><Building2 className="text-[#c9a84c]/25" size={56} /></div>;
  }
  return (
    <div className="space-y-2">
      <div className="relative aspect-[16/10] overflow-hidden rounded-2xl border border-white/[0.06] bg-black">
        <AnimatePresence mode="wait">
          <motion.img key={i} src={imgUrl(images[i])} alt={title} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }} className="absolute inset-0 h-full w-full object-cover" />
        </AnimatePresence>
      </div>
      {images.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {images.map((u, k) => (
            <button key={u} type="button" onClick={() => setI(k)} aria-label={`Foto ${k + 1}`}
              className={`h-16 w-24 flex-shrink-0 overflow-hidden rounded-lg border ${k === i ? 'border-[#c9a84c]' : 'border-white/10 opacity-70 hover:opacity-100'}`}>
              <img src={imgUrl(u)} alt="" className="h-full w-full object-cover" loading="lazy" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Fact({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 rounded-xl border border-white/[0.06] bg-white/[0.02] p-3.5">
      <span className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-[#c9a84c]/10"><Icon size={15} className="text-[#c9a84c]" /></span>
      <div className="min-w-0">
        <p className="text-[11px] font-semibold text-[#c9a84c]">{label}</p>
        <p className="text-sm text-white leading-snug">{value}</p>
      </div>
    </div>
  );
}

function Section({ title, children, testid }) {
  return (
    <section className={`${card} p-5 space-y-4`} data-testid={testid}>
      <h3 className="text-white font-semibold">{title}</h3>
      {children}
    </section>
  );
}

function InterestModal({ o, onClose, onDone }) {
  const [amount, setAmount] = useState(String(o.my_interest?.amount || o.min_contribution));
  const [message, setMessage] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const n = Number(amount);
  const ok = n >= o.min_contribution && accepted;
  const submit = async (e) => {
    e.preventDefault();
    if (!ok) return;
    setBusy(true);
    try {
      await opportunitiesApi.interest(o.id, { amount: n, message: message || null, accepted });
      toast.success('Solicitud enviada. El equipo del club se pondrá en contacto contigo.');
      onDone();
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" role="dialog" aria-modal="true" aria-labelledby="interest-title">
      <motion.form initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} onSubmit={submit}
        className="w-full max-w-md space-y-4 rounded-2xl border border-white/10 bg-[#0d1829] p-5 shadow-2xl" data-testid="interest-modal">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 id="interest-title" className="text-white font-semibold">Solicitar participación</h3>
            <p className="text-xs text-gray-400 mt-0.5">{opTitle(o)}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Cerrar"><X size={18} className="text-gray-400 hover:text-white" /></button>
        </div>
        <label className="block space-y-1">
          <span className="text-[11px] uppercase tracking-wider font-semibold text-gray-400">Aportación que te planteas ({o.currency === 'USD' ? '$' : '€'})</span>
          <input type="number" min={o.min_contribution} step="1" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="interest-amount"
            className="w-full rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2.5 text-white focus:border-[#c9a84c]/50 focus:outline-none" />
          <span className={`text-xs ${n && n < o.min_contribution ? 'text-red-300' : 'text-gray-500'}`}>Aportación mínima: {money(o.min_contribution, o.currency)}</span>
        </label>
        <label className="block space-y-1">
          <span className="text-[11px] uppercase tracking-wider font-semibold text-gray-400">Comentario (opcional)</span>
          <textarea rows={2} value={message} onChange={(e) => setMessage(e.target.value)} maxLength={1000}
            className="w-full resize-y rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2 text-sm text-white focus:border-[#c9a84c]/50 focus:outline-none" />
        </label>
        <label className="flex items-start gap-2.5 text-xs leading-relaxed text-gray-300">
          <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} className="mt-0.5 accent-[#c9a84c]" data-testid="interest-accept" />
          <span>Entiendo que esta solicitud no es vinculante ni constituye una aportación. Si sigo adelante, la participación se formaliza con el contrato correspondiente y la documentación completa de la operación.</span>
        </label>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-lg border border-white/10 px-4 py-2 text-sm text-gray-300 hover:text-white">Cancelar</button>
          <button type="submit" disabled={!ok || busy} data-testid="interest-submit"
            className="inline-flex items-center gap-2 rounded-lg bg-[#c9a84c] px-4 py-2 text-sm font-semibold text-[#0a1628] disabled:opacity-40">
            {busy && <Loader2 size={14} className="animate-spin" />} Enviar solicitud
          </button>
        </div>
      </motion.form>
    </div>
  );
}

export default function DashboardPropertyDetail() {
  const { id } = useParams();
  const [o, setO] = useState(null);
  const [error, setError] = useState(null);
  const [asking, setAsking] = useState(false);
  const load = useCallback(() => opportunitiesApi.get(id).then(setO).catch((e) => setError(e.message)), [id]);
  useEffect(() => { setO(null); setError(null); load(); }, [load]);

  if (error) {
    return (
      <div className="space-y-6">
        <Link to="/dashboard/propiedades" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white"><ArrowLeft size={16} /> Oportunidades</Link>
        <EmptyState icon={Building2} title="No hemos encontrado esta oportunidad" text={error} />
      </div>
    );
  }
  if (!o) return <PageSkeleton cards={0} rows={5} />;

  const cur = o.currency;
  const ret = returnFigure(o);
  const sale = saleFigure(o);
  const costTotal = (o.cost_breakdown || []).reduce((a, c) => a + (Number(c.amount) || 0), 0);
  const mr = o.market_reference;
  const full = (o.progress?.pct ?? 0) >= 100;

  return (
    <div className="space-y-6" data-testid="opportunity-detail">
      <Link to="/dashboard/propiedades" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors"><ArrowLeft size={16} /> Oportunidades</Link>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1.35fr)_minmax(300px,1fr)] items-start">
        <div className="space-y-6 min-w-0">
          <Gallery images={o.images || []} title={opTitle(o)} />
          <div className="space-y-2">
            <span className={`inline-block rounded-full border px-2.5 py-1 text-[11px] font-semibold ${STATUS_STYLE[o.status] || ''}`}>{o.status}</span>
            <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">{opTitle(o)}</h1>
            {o.tagline && <p className="text-xs uppercase tracking-[0.25em] text-[#c9a84c]">{o.tagline}</p>}
            <p className="flex items-center gap-1.5 text-sm text-gray-300"><MapPin size={14} className="text-[#c9a84c]" /> {opPlace(o)}</p>
            {o.description && <p className="text-sm text-gray-300 leading-relaxed whitespace-pre-line pt-1">{o.description}</p>}
          </div>

          <Section title="Ficha de la operación" testid="op-facts">
            <div className="grid gap-3 sm:grid-cols-2">
              <Fact icon={MapPin} label="Ubicación" value={opPlace(o)} />
              <Fact icon={Home} label="Tipo de activo" value={o.asset_type} />
              <Fact icon={Maximize2} label="Superficie" value={o.surface} />
              <Fact icon={Wrench} label="Estado del proyecto" value={o.project_type} />
              <Fact icon={CalendarClock} label="Plazo estimado del proyecto" value={o.term} />
              <Fact icon={Layers} label="Unidades" value={o.units} />
              <Fact icon={Coins} label="Inversión total estimada" value={o.total_investment ? money(o.total_investment, cur) : null} />
              <Fact icon={BarChart3} label="Precio medio de la zona" value={o.zone_avg_price ? money(o.zone_avg_price, cur) : null} />
              {sale && <Fact icon={Tag} label={sale.label} value={sale.value} />}
              {(o.features || []).map((f) => <Fact key={f.label} icon={ListChecks} label={f.label} value={f.value} />)}
            </div>
          </Section>

          {ret && (
            <section className="rounded-2xl border border-[#c9a84c]/30 bg-gradient-to-br from-[#c9a84c]/[0.12] to-transparent p-5" data-testid="op-return">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-[#c9a84c]/15"><TrendingUp size={22} className="text-[#e3c87a]" /></span>
                <div className="flex-1">
                  <p className="text-xs uppercase tracking-wider font-semibold text-[#c9a84c]">{ret.label}</p>
                  <p className="text-3xl font-bold text-[#e3c87a] tabular-nums">{ret.value}</p>
                  {o.scenario && <p className="text-xs text-gray-300 mt-0.5">Escenario {o.scenario.toLowerCase()}</p>}
                </div>
                {o.return_note && <p className="flex items-start gap-2 text-xs text-gray-300 sm:max-w-[260px] leading-relaxed"><FileText size={14} className="mt-0.5 flex-shrink-0 text-[#c9a84c]" /> {o.return_note}</p>}
              </div>
            </section>
          )}

          {o.cost_breakdown?.length > 0 && (
            <Section title="Inversión necesaria y estructura de costes" testid="op-costs">
              <div className="overflow-hidden rounded-xl border border-white/[0.06]">
                <table className="w-full text-sm">
                  <tbody>
                    {o.cost_breakdown.map((c) => (
                      <tr key={c.label} className="border-b border-white/[0.04]">
                        <td className="px-4 py-2.5 text-gray-300">{c.label}</td>
                        <td className="px-4 py-2.5 text-right text-white tabular-nums">{money(c.amount, cur)}</td>
                      </tr>
                    ))}
                    <tr className="bg-[#c9a84c]/10">
                      <td className="px-4 py-3 font-semibold text-[#e3c87a]">Total inversión</td>
                      <td className="px-4 py-3 text-right font-bold text-[#e3c87a] tabular-nums">{money(costTotal, cur)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-gray-500">Estimación aproximada de los gastos de la operación.</p>
              {(o.vehicle || o.partner) && (
                <div className="grid gap-3 sm:grid-cols-2">
                  <Fact icon={UserRound} label="Vehículo" value={o.vehicle} />
                  <Fact icon={Handshake} label="Operador" value={o.partner ? `Operación presentada por Freedom Homes Club en colaboración con ${o.partner}.` : null} />
                </div>
              )}
            </Section>
          )}

          {(o.market_note || mr || o.comparables?.length > 0) && (
            <Section title="Estudio del municipio y referencias de mercado" testid="op-market">
              {o.market_note && <p className="text-sm text-gray-300 leading-relaxed">{o.market_note}</p>}
              {mr && (
                <div className="overflow-x-auto rounded-xl border border-white/[0.06]">
                  <table className="w-full min-w-[460px] text-sm">
                    <thead><tr className="border-b border-white/[0.06] text-left text-[11px] uppercase tracking-wider text-[#c9a84c]">
                      <th className="px-4 py-2.5">Activo</th><th className="px-4 py-2.5 text-right">P.V.P. mercado medio</th><th className="px-4 py-2.5 text-right">{mr.range_label || 'Rango'}</th>
                    </tr></thead>
                    <tbody><tr>
                      <td className="px-4 py-3 text-gray-300">{mr.label || '—'}</td>
                      <td className="px-4 py-3 text-right text-white tabular-nums">{mr.average ? money(mr.average, cur) : '—'}</td>
                      <td className="px-4 py-3 text-right text-white tabular-nums">{mr.range_min ? `${money(mr.range_min, cur)} – ${money(mr.range_max, cur)}` : '—'}</td>
                    </tr></tbody>
                  </table>
                </div>
              )}
              {o.comparables?.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {o.comparables.map((c) => (
                    <div key={c.label} className="rounded-xl border border-white/[0.06] bg-white/[0.02] px-3 py-2">
                      <p className="text-[11px] text-gray-400">{c.label}</p>
                      <p className="text-sm font-semibold text-white tabular-nums">{money(c.amount, cur)}</p>
                    </div>
                  ))}
                </div>
              )}
            </Section>
          )}

          <p className="flex items-start gap-2 text-[11px] leading-relaxed text-gray-500"><Info size={13} className="mt-0.5 flex-shrink-0" /> {DISCLAIMER}</p>
        </div>

        {/* Aportaciones */}
        <aside className={`${card} p-5 space-y-5 lg:sticky lg:top-4`} data-testid="op-funding">
          <h3 className="text-white font-semibold">Participación en la operación</h3>
          <FundingBar progress={o.progress} currency={cur} size="lg" />
          <div className={`grid gap-3 ${full ? 'grid-cols-1' : 'grid-cols-2'}`}>
            {!full && <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
              <p className="text-[11px] text-gray-400">Aportación mínima</p>
              <p className="text-lg font-bold text-[#e3c87a] tabular-nums" data-testid="op-min">{money(o.min_contribution, cur)}</p>
            </div>}
            <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
              <p className="text-[11px] text-gray-400">Divisa</p>
              <p className="text-lg font-bold text-white">{cur === 'USD' ? 'Dólares ($)' : 'Euros (€)'}</p>
            </div>
          </div>
          {!full && o.min_reason === 'access' && (
            <p className="text-xs text-gray-400" data-testid="op-min-access">
              Mínimo para socios Access. Con PRO o Premium puedes aportar desde {money(o.min_contribution_base, cur)}.
            </p>
          )}
          {o.progress?.target && !full && (
            <p className="text-xs text-gray-400">Objetivo de aportaciones: <span className="text-white tabular-nums">{money(o.progress.target, cur)}</span></p>
          )}
          {o.my_interest && (
            <p className="flex items-start gap-2 rounded-xl border border-emerald-500/25 bg-emerald-500/10 p-3 text-xs text-emerald-200">
              <CheckCircle2 size={14} className="mt-0.5 flex-shrink-0" />
              Solicitud enviada por {money(o.my_interest.amount, cur)}. El equipo del club se pondrá en contacto contigo.
            </p>
          )}
          {o.accepts_interest ? (
            <button type="button" onClick={() => setAsking(true)} data-testid="op-interest"
              className="w-full rounded-xl bg-[#c9a84c] px-4 py-3 text-sm font-semibold text-[#0a1628] hover:bg-[#d6b75a]">
              {o.my_interest ? 'Modificar mi solicitud' : 'Solicitar participación'}
            </button>
          ) : (
            <p className="rounded-xl border border-white/[0.06] bg-white/[0.02] px-3 py-2.5 text-xs text-gray-300">
              {full ? 'Esta operación ya está financiada al 100 %.' : 'Esta operación no admite nuevas solicitudes en este momento.'}
            </p>
          )}
          <p className="text-[11px] leading-relaxed text-gray-500">Las aportaciones se formalizan mediante contrato privado con la documentación completa de la operación. Cada socio decide en qué operación participa.</p>
        </aside>
      </div>

      {asking && <InterestModal o={o} onClose={() => setAsking(false)} onDone={() => { setAsking(false); load(); }} />}
    </div>
  );
}
